import React, { useState, useEffect } from 'react';
import { Activity, RotateCcw, Clock, TrendingUp, Zap, Server } from 'lucide-react';
import apiService from '../services/api';
import { MetricsResponse, EndpointMetric } from '../types/api';

// Method badge colors
const methodColors: Record<string, { bg: string; text: string; border: string }> = {
  GET: { bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
  POST: { bg: '#dcfce7', text: '#15803d', border: '#86efac' },
  PUT: { bg: '#fef3c7', text: '#b45309', border: '#fcd34d' },
  DELETE: { bg: '#fee2e2', text: '#dc2626', border: '#fca5a5' },
  PATCH: { bg: '#f3e8ff', text: '#7c3aed', border: '#c4b5fd' },
};

const Metrics: React.FC = () => {
  const [metrics, setMetrics] = useState<MetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resetting, setResetting] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getMetrics();
      setMetrics(data);
      setLastUpdated(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset all metrics? This action cannot be undone.')) {
      return;
    }

    try {
      setResetting(true);
      await apiService.resetMetrics();
      await fetchMetrics();
    } catch (err: any) {
      setError(err.message || 'Failed to reset metrics');
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, []);

  const maxRequestCount = metrics?.endpoints?.length
    ? Math.max(...metrics.endpoints.map(e => e.requestCount))
    : 0;

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getMethodStyle = (method: string) => {
    return methodColors[method.toUpperCase()] || { bg: '#f1f5f9', text: '#475569', border: '#cbd5e1' };
  };

  // Group endpoints by base path for better organization
  const groupedEndpoints = metrics?.endpoints?.reduce((acc, endpoint) => {
    const basePath = endpoint.endpoint.split('/')[1] || 'root';
    if (!acc[basePath]) acc[basePath] = [];
    acc[basePath].push(endpoint);
    return acc;
  }, {} as Record<string, EndpointMetric[]>) || {};

  if (loading && !metrics) {
    return (
      <div className="metrics-loading">
        <div className="metrics-loading-spinner"></div>
        <span>Loading metrics...</span>
      </div>
    );
  }

  return (
    <div className="metrics-container">
      {/* Header */}
      <div className="metrics-header">
        <div className="metrics-header-info">
          <h2 className="metrics-title">
            <Activity size={28} className="metrics-title-icon" />
            API Metrics Dashboard
          </h2>
          <p className="metrics-subtitle">
            Real-time endpoint request monitoring • Resets on server restart
          </p>
        </div>
        <div className="metrics-actions">
          <button
            onClick={fetchMetrics}
            className="btn btn-secondary metrics-refresh-btn"
            disabled={loading}
          >
            <RotateCcw size={16} className={loading ? 'spin' : ''} />
            Refresh
          </button>
          <button
            onClick={handleReset}
            className="btn btn-danger"
            disabled={resetting}
          >
            <Zap size={16} />
            {resetting ? 'Resetting...' : 'Reset All'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error" style={{ marginBottom: '1.5rem' }}>
          {error}
        </div>
      )}

      {/* Summary Cards */}
      <div className="metrics-summary-grid">
        <div className="metrics-summary-card metrics-summary-total">
          <div className="metrics-summary-icon">
            <TrendingUp size={24} />
          </div>
          <div className="metrics-summary-content">
            <span className="metrics-summary-value">{metrics?.totalRequests?.toLocaleString() || 0}</span>
            <span className="metrics-summary-label">Total Requests</span>
          </div>
        </div>

        <div className="metrics-summary-card metrics-summary-endpoints">
          <div className="metrics-summary-icon">
            <Server size={24} />
          </div>
          <div className="metrics-summary-content">
            <span className="metrics-summary-value">{metrics?.endpointCount || 0}</span>
            <span className="metrics-summary-label">Active Endpoints</span>
          </div>
        </div>

        <div className="metrics-summary-card metrics-summary-updated">
          <div className="metrics-summary-icon">
            <Clock size={24} />
          </div>
          <div className="metrics-summary-content">
            <span className="metrics-summary-value">
              {lastUpdated ? lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '--'}
            </span>
            <span className="metrics-summary-label">Last Updated</span>
          </div>
        </div>
      </div>

      {/* Chart Section */}
      {metrics?.endpoints && metrics.endpoints.length > 0 ? (
        <div className="metrics-chart-container">
          <h3 className="metrics-chart-title">Request Distribution by Endpoint</h3>
          
          <div className="metrics-bars">
            {metrics.endpoints
              .sort((a, b) => b.requestCount - a.requestCount)
              .map((endpoint, index) => {
                const percentage = maxRequestCount > 0 ? (endpoint.requestCount / maxRequestCount) * 100 : 0;
                const methodStyle = getMethodStyle(endpoint.method);
                
                return (
                  <div
                    key={`${endpoint.method}-${endpoint.endpoint}`}
                    className="metrics-bar-row"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="metrics-bar-info">
                      <span
                        className="metrics-method-badge"
                        style={{
                          backgroundColor: methodStyle.bg,
                          color: methodStyle.text,
                          border: `1px solid ${methodStyle.border}`,
                        }}
                      >
                        {endpoint.method}
                      </span>
                      <span className="metrics-endpoint-path">{endpoint.endpoint}</span>
                    </div>
                    
                    <div className="metrics-bar-wrapper">
                      <div className="metrics-bar-track">
                        <div
                          className="metrics-bar-fill"
                          style={{
                            width: `${percentage}%`,
                            background: `linear-gradient(90deg, ${methodStyle.border} 0%, ${methodStyle.text} 100%)`,
                          }}
                        />
                      </div>
                      <span className="metrics-bar-count">{endpoint.requestCount.toLocaleString()}</span>
                    </div>
                    
                    <div className="metrics-bar-timestamp">
                      <Clock size={12} />
                      {formatTimestamp(endpoint.lastRequestAt)}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      ) : (
        <div className="metrics-empty">
          <Activity size={48} className="metrics-empty-icon" />
          <h3>No Metrics Yet</h3>
          <p>Metrics will appear here once API endpoints receive requests.</p>
        </div>
      )}

      {/* Grouped View */}
      {Object.keys(groupedEndpoints).length > 0 && (
        <div className="metrics-grouped-section">
          <h3 className="metrics-chart-title">Endpoints by Category</h3>
          <div className="metrics-grouped-grid">
            {Object.entries(groupedEndpoints)
              .sort(([, a], [, b]) => {
                const totalA = a.reduce((sum, e) => sum + e.requestCount, 0);
                const totalB = b.reduce((sum, e) => sum + e.requestCount, 0);
                return totalB - totalA;
              })
              .map(([category, endpoints]) => {
                const totalRequests = endpoints.reduce((sum, e) => sum + e.requestCount, 0);
                return (
                  <div key={category} className="metrics-category-card">
                    <div className="metrics-category-header">
                      <span className="metrics-category-name">/{category}</span>
                      <span className="metrics-category-total">{totalRequests.toLocaleString()} requests</span>
                    </div>
                    <div className="metrics-category-endpoints">
                      {endpoints.map(ep => {
                        const methodStyle = getMethodStyle(ep.method);
                        return (
                          <div key={`${ep.method}-${ep.endpoint}`} className="metrics-category-item">
                            <span
                              className="metrics-method-badge-sm"
                              style={{
                                backgroundColor: methodStyle.bg,
                                color: methodStyle.text,
                              }}
                            >
                              {ep.method}
                            </span>
                            <span className="metrics-category-path">{ep.endpoint}</span>
                            <span className="metrics-category-count">{ep.requestCount}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      <style>{`
        .metrics-container {
          max-width: 1400px;
          margin: 0 auto;
        }

        .metrics-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem;
          color: #64748b;
          gap: 1rem;
        }

        .metrics-loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #e2e8f0;
          border-top-color: #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .metrics-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .metrics-title {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-size: 1.75rem;
          font-weight: 700;
          color: #1e293b;
          margin: 0 0 0.5rem 0;
        }

        .metrics-title-icon {
          color: #667eea;
        }

        .metrics-subtitle {
          color: #64748b;
          font-size: 0.95rem;
          margin: 0;
        }

        .metrics-actions {
          display: flex;
          gap: 0.75rem;
        }

        .metrics-refresh-btn .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .metrics-summary-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
          gap: 1.25rem;
          margin-bottom: 2rem;
        }

        .metrics-summary-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1.5rem;
          border-radius: 16px;
          background: white;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          border: 1px solid #e2e8f0;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .metrics-summary-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
        }

        .metrics-summary-icon {
          width: 52px;
          height: 52px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .metrics-summary-total .metrics-summary-icon {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .metrics-summary-endpoints .metrics-summary-icon {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
        }

        .metrics-summary-updated .metrics-summary-icon {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          color: white;
        }

        .metrics-summary-content {
          display: flex;
          flex-direction: column;
        }

        .metrics-summary-value {
          font-size: 1.75rem;
          font-weight: 700;
          color: #1e293b;
          line-height: 1.2;
        }

        .metrics-summary-label {
          font-size: 0.875rem;
          color: #64748b;
          font-weight: 500;
        }

        .metrics-chart-container {
          background: white;
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          border: 1px solid #e2e8f0;
          margin-bottom: 2rem;
        }

        .metrics-chart-title {
          font-size: 1.25rem;
          font-weight: 600;
          color: #1e293b;
          margin: 0 0 1.5rem 0;
        }

        .metrics-bars {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .metrics-bar-row {
          display: grid;
          grid-template-columns: 280px 1fr 120px;
          align-items: center;
          gap: 1.25rem;
          padding: 0.75rem 1rem;
          background: #f8fafc;
          border-radius: 10px;
          animation: slideIn 0.4s ease-out forwards;
          opacity: 0;
          transform: translateX(-20px);
        }

        @keyframes slideIn {
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .metrics-bar-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          min-width: 0;
        }

        .metrics-method-badge {
          padding: 0.25rem 0.6rem;
          border-radius: 6px;
          font-size: 0.7rem;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          flex-shrink: 0;
          min-width: 50px;
          text-align: center;
        }

        .metrics-endpoint-path {
          font-family: 'SF Mono', 'Fira Code', monospace;
          font-size: 0.85rem;
          color: #334155;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .metrics-bar-wrapper {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .metrics-bar-track {
          flex: 1;
          height: 24px;
          background: #e2e8f0;
          border-radius: 12px;
          overflow: hidden;
        }

        .metrics-bar-fill {
          height: 100%;
          border-radius: 12px;
          transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
          min-width: 4px;
        }

        .metrics-bar-count {
          font-weight: 700;
          font-size: 0.95rem;
          color: #1e293b;
          min-width: 60px;
          text-align: right;
        }

        .metrics-bar-timestamp {
          display: flex;
          align-items: center;
          gap: 0.4rem;
          color: #94a3b8;
          font-size: 0.75rem;
          white-space: nowrap;
        }

        .metrics-empty {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 16px;
          border: 2px dashed #e2e8f0;
          color: #64748b;
          text-align: center;
        }

        .metrics-empty-icon {
          color: #cbd5e1;
          margin-bottom: 1rem;
        }

        .metrics-empty h3 {
          margin: 0 0 0.5rem 0;
          color: #475569;
        }

        .metrics-empty p {
          margin: 0;
        }

        .metrics-grouped-section {
          margin-top: 2rem;
        }

        .metrics-grouped-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
          gap: 1.25rem;
        }

        .metrics-category-card {
          background: white;
          border-radius: 12px;
          padding: 1.25rem;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
          border: 1px solid #e2e8f0;
        }

        .metrics-category-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 0.75rem;
          margin-bottom: 0.75rem;
          border-bottom: 1px solid #f1f5f9;
        }

        .metrics-category-name {
          font-weight: 600;
          font-size: 1rem;
          color: #667eea;
        }

        .metrics-category-total {
          font-size: 0.8rem;
          color: #64748b;
          background: #f1f5f9;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
        }

        .metrics-category-endpoints {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .metrics-category-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.4rem;
          border-radius: 6px;
          transition: background 0.15s;
        }

        .metrics-category-item:hover {
          background: #f8fafc;
        }

        .metrics-method-badge-sm {
          padding: 0.15rem 0.4rem;
          border-radius: 4px;
          font-size: 0.6rem;
          font-weight: 700;
          text-transform: uppercase;
          min-width: 36px;
          text-align: center;
        }

        .metrics-category-path {
          flex: 1;
          font-family: 'SF Mono', 'Fira Code', monospace;
          font-size: 0.75rem;
          color: #475569;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .metrics-category-count {
          font-weight: 600;
          font-size: 0.8rem;
          color: #1e293b;
        }

        @media (max-width: 768px) {
          .metrics-bar-row {
            grid-template-columns: 1fr;
            gap: 0.75rem;
          }

          .metrics-bar-wrapper {
            order: 1;
          }

          .metrics-bar-timestamp {
            order: 2;
            justify-content: flex-end;
          }

          .metrics-header {
            flex-direction: column;
          }

          .metrics-actions {
            width: 100%;
          }

          .metrics-actions .btn {
            flex: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default Metrics;

